import React from 'react';
import Button from '@atlaskit/button';
import { IconButton } from '@atlaskit/button/new';
import Tooltip from '@atlaskit/tooltip';
import log from '@atlassian/jira-common-util-logging/src/log.tsx';
import { useIntl } from '@atlassian/jira-intl';
import {
	NEWEST_FIRST,
	OLDEST_FIRST,
	type ActivitySortOrderType,
} from '@atlassian/jira-issue-shared-types/src/common/types/activity-sort-order-type.tsx';
import {
	ContextualAnalyticsData,
	SCREEN,
	fireScreenAnalytics,
	MountEvent,
	useAnalyticsEvents,
} from '@atlassian/jira-product-analytics-bridge';
import { isVisualRefreshEnabled } from '@atlassian/jira-visual-refresh-rollout/src/feature-switch/index.tsx';
import type { SelectActivitySortOrder } from '../../common/ui/types.tsx';
import { messages } from './messages.tsx';
import { getSortOptions } from './utils.tsx';

export type Props = {
	isDisabled?: boolean;
	selectedSortOrder: ActivitySortOrderType;
	onSelectSortOrder: SelectActivitySortOrder;
};

export const ActivitySortingToggle = (props: Props) => {
	const { selectedSortOrder, onSelectSortOrder, isDisabled = false } = props;
	const { formatMessage } = useIntl();
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const sortOptions = getSortOptions(formatMessage);
	const selectedSortOption = sortOptions.find((item) => item.type === selectedSortOrder);

	if (!selectedSortOption) {
		log.safeErrorWithoutCustomerData(
			'issue.activity.activity-sorting.select-sort-order',
			`Failed to select a sort option with selected sort of ${selectedSortOrder}`,
		);
		return null;
	}

	const onToggle = () => {
		const analyticsEvent = createAnalyticsEvent({
			action: 'clicked',
			actionSubject: 'button',
		});
		if (selectedSortOrder === NEWEST_FIRST)
			onSelectSortOrder(OLDEST_FIRST, analyticsEvent, 'sortOrderItemToggled');
		if (selectedSortOrder === OLDEST_FIRST)
			onSelectSortOrder(NEWEST_FIRST, analyticsEvent, 'sortOrderItemToggled');
	};

	return (
		<ContextualAnalyticsData sourceType={SCREEN} sourceName="activityFeedSort">
			<MountEvent onMount={fireScreenAnalytics} />
			<Tooltip content={formatMessage(messages.tooltip)} hideTooltipOnMouseDown position="bottom">
				{isVisualRefreshEnabled() ? (
					<IconButton
						appearance="subtle"
						isDisabled={isDisabled}
						onClick={onToggle}
						icon={() => selectedSortOption.icon}
						label={selectedSortOption.name}
						testId="issue-activity-feed.ui.activity-sorting-toggle.sorting-button"
						interactionName="auto-generated.issue-activity-feed.ui.activity-sorting-toggle.sorting-button.button"
					/>
				) : (
					<Button
						appearance="subtle"
						isDisabled={isDisabled}
						onClick={onToggle}
						// @ts-expect-error - TS2322 - Type 'ReactNode' is not assignable to type 'ReactChild | undefined'.
						iconAfter={selectedSortOption.icon}
						spacing="compact"
						testId="issue-activity-feed.ui.activity-sorting-toggle.sorting-button"
						interactionName="auto-generated.issue-activity-feed.ui.activity-sorting-toggle.sorting-button.button"
					>
						{selectedSortOption.name}
					</Button>
				)}
			</Tooltip>
		</ContextualAnalyticsData>
	);
};

export default ActivitySortingToggle;
